import React, { FC, forwardRef, useContext, useMemo } from 'react';
import hoistNonReactStatics from 'hoist-non-react-statics';

import { ThemeContext } from 'styled-components';
import requiredValidator from '../../../validators/requiredValidator';
import Control from '../Control';
import { RadioListControlComposedProps, RadioListControlDefaultProps, RadioListControlValidationProps } from './RadioListControl';

const withControl = <Value, StateValue>(WrappedComponent: FC<RadioListControlComposedProps<Value, StateValue>>) => {
  const WithControl = forwardRef<HTMLInputElement, RadioListControlComposedProps<Value, StateValue>>(({
    name, readOnly, textOnly, required, recommended, noBox, ...props
  }, ref) => {
    const theme = useContext(ThemeContext);
    const resolvedNoBox = (typeof noBox !== 'undefined') ? noBox : theme?.RadioList?.noBox;

    const defaultValidators = useMemo(() => ({
      required: requiredValidator,
      recommended: { validator: requiredValidator, type: 'warning' },
    }), []);

    return (
      <Control<Value, StateValue, HTMLInputElement, RadioListControlValidationProps>
        {...props}
        required={required}
        recommended={recommended}
        name={name}
        defaultValidators={defaultValidators}
        errorsMarginTop={resolvedNoBox ? '16px' : undefined}
        readOnly={readOnly}
        textOnly={textOnly}
      >
        {({
          id, value, isValid, hasWarning, hasCaution, hasInfo, isTouched, errorMessagesId, onValueChange,
        }) => (
          <WrappedComponent
            id={id}
            data-control
            {...props}
            noBox={noBox}
            name={name}
            value={value}
            isValid={isValid}
            hasWarning={hasWarning}
            hasCaution={hasCaution}
            hasInfo={hasInfo}
            isTouched={isTouched}
            errorMessagesId={errorMessagesId}
            onValueChange={onValueChange}
            readOnly={readOnly}
            textOnly={textOnly}
            required={required}
            ref={ref}
          />
        )}
      </Control>
    );
  });

  WithControl.defaultProps = RadioListControlDefaultProps;

  return hoistNonReactStatics(WithControl, WrappedComponent);
};

export default withControl;
